import { createBlockJamManifest } from '@puzzlemania/blockjam';
import type { IPluginManifest } from '@puzzlemania/core';
import { Logger } from '@puzzlemania/shared';
import rawBlockJamConfig from 'virtual:blockjam-config';
import { coinIconUrl, tutorialHandUrl } from '../assets';
import { resolveBlockJamConfig } from './blockjamConfig';
import { createBackgroundSolver } from './blockjamSolver';
import { currentPerfOverlayFlag } from './perfOverlayFlag';

/**
 * Module 36 (WP-36.2) — the one place the app turns its build-time Block Jam
 * config into a registered plugin manifest.
 *
 * Split from `blockjamConfig.ts` on purpose: that module is pure and takes the
 * manifest as an argument so it can be unit-tested without a Vite build, while
 * this one owns the `virtual:blockjam-config` import that only resolves inside a
 * Vite graph. Both app entries (the main site and the portal build) go through
 * here, so neither can drift onto stale in-bundle defaults.
 *
 * Resolved once at module scope: the config is build-time constant, and
 * re-validating it per registration would repeat the Zod parse for nothing.
 */
export const blockJamConfig = resolveBlockJamConfig(
  rawBlockJamConfig,
  // Its own instance: config resolution happens at import time, before the app
  // root builds the logger it hands to the error boundary, and a validation
  // warning that never surfaced would make a silently-ignored config
  // undiagnosable.
  new Logger(import.meta.env.DEV),
);

/** Block Jam's manifest, bound to the config the build actually shipped. */
export function createConfiguredBlockJamManifest(isEnabled = true): IPluginManifest {
  const solver = createBackgroundSolver();
  return createBlockJamManifest(isEnabled, {
    generation: blockJamConfig.generation,
    visuals: blockJamConfig.visuals,
    boardVisuals: blockJamConfig.boardVisuals,
    // Resolved per registration, not at module scope: `?perf=1` has to take
    // effect on the load that carries it, and the manifest is built after the
    // URL is known (WP-36.11).
    perfOverlay: currentPerfOverlayFlag(),
    // Module 44 (WP-44.1) — shared app art. This is the ONLY place either build
    // hands the game an asset URL, and both entries register through this
    // function, so the main site and the CrazyGames embed cannot end up on
    // different art, or on none. `blockjam-assets.test.ts` holds that line.
    assets: { tutorialHandUrl, coinIconUrl },
    // Module 83 (WP-83.5b) — the route solver off the main thread. Both entries
    // register through this function, so the main site and the Bridge embed get
    // the worker alike; a host without `Worker` gets `undefined` and searches
    // on the main thread.
    ...(solver ? { solver } : {}),
  });
}
